import * as THREE from 'three/webgpu';

import { mulberry32, hashSeed } from '../math/rng.js';

/**
 * @file The lit-window atlas: a canvas of glowing window grids that
 * `render/materials.js -> emissiveShellMaterial()` samples for the ONE extra
 * quad per camera-facing facade. Invisible by day (`uNight` gates both colour
 * and alpha), a bloom source after dusk.
 *
 * Generic engine asset — the city uses it for towers, the forest could use it
 * for a cabin window, the wasteland for a lit bunker hatch. Nothing in here
 * knows what a "building" is.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LAYOUT, AND WHY IT IS A SINGLE COLUMN
 * ─────────────────────────────────────────────────────────────────────────────
 * A facade quad needs its windows to TILE in two independent ways:
 *
 *   • horizontally — a 9 m shopfront and a 40 m slab must both get ~3 m bays,
 *   • vertically   — one row of windows per storey, whatever the storey count.
 *
 * `emissiveShellMaterial` samples `texture(mask, uv * uvScale)` with no `frac()`
 * anywhere, so tiling has to come from the UVs, and UVs cannot tile inside an
 * atlas cell without bleeding into the neighbouring cell. The way out is to make
 * the atlas ONE CELL WIDE:
 *
 *   • `wrapS = RepeatWrapping` — u may run 0..N and always lands back in the one
 *     column, so bay pitch is `facadeWidth / (cols * bayMetres)` and stays
 *     constant no matter how wide the wall is.
 *   • `wrapT = ClampToEdgeWrapping` — v selects the VARIANT, exactly once.
 *
 * That leaves the storey count. Instead of tiling v, every variant carries a
 * DIFFERENT number of window rows ({@link floorRowsFor}: 2 .. 16 on a geometric
 * ramp), and the caller picks the variant whose row count is closest to the
 * building's storey count ({@link WindowAtlas#pickVariant}). A 7-storey block
 * gets the 7-row variant and every row is a floor; an 18-storey tower gets the
 * 16-row one and is stretched by 12%, which nobody has ever seen.
 *
 * Cells are therefore VARIABLE HEIGHT (`rows * rowPx`), which keeps the texel
 * density per storey identical across variants — a 2-row cell squeezed into the
 * same box as a 16-row cell would be eight times blurrier.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SAMPLING RULES (get these wrong and it looks like a texture bug)
 * ─────────────────────────────────────────────────────────────────────────────
 * • `NearestFilter`, `generateMipmaps: false` — per the plan, masks are never
 *   filtered. Nearest is also what makes the single-column trick safe: no
 *   neighbouring texel is ever blended in.
 * • sRGB colour space: the RGB channels are authored as lamp colours.
 * • `uvFor()` insets v by half a texel and every cell is drawn with a 1 px
 *   transparent gutter top and bottom, so a variant can never sample its
 *   neighbour even under a rounding error.
 * • Canvas rows run top-down, texture `v` runs bottom-up (`flipY` defaults to
 *   true), so {@link WindowAtlas#uvFor} returns v ALREADY FLIPPED. Callers use
 *   the numbers as-is.
 *
 * @module engine/assets/windowMasks
 */

/** @typedef {{u0: number, v0: number, u1: number, v1: number}} UVRect */

/**
 * @typedef {Object} AtlasCell
 * @property {number} index
 * @property {number} rows Window rows in this cell = storeys it represents.
 * @property {number} cols Window bays across.
 * @property {number} x @property {number} y Pixel origin, TOP-LEFT (canvas space).
 * @property {number} w @property {number} h Pixel size.
 */

/**
 * @typedef {Object} AtlasLayout
 * @property {number} width @property {number} height Texture size, pixels.
 * @property {number} cols Bays per cell.
 * @property {number} rowPx Pixels per window row.
 * @property {AtlasCell[]} cells
 */

/** Defaults, exported so tests and callers agree on the numbers. */
export const WINDOW_ATLAS_DEFAULTS = Object.freeze({
  variants: 8,
  cellPx: 64,
  /** Window bays across one horizontal repeat. */
  cols: 4,
  /** Metres of facade one bay covers — 3.2 m, the project's storey height, so a
   *  window is roughly as wide as it is tall. */
  bayMetres: 3.2,
  /** Fraction of windows that are DARK (nobody home). */
  darkChance: 0.34,
  /** Fraction of the LIT ones that read as broken/blown out. */
  brokenChance: 0.11,
  /** Fraction that get the cold blue-white "screen" colour. */
  screenChance: 0.09,
});

/**
 * Window rows in variant `i` of `n`. Geometric from 2 to 16 so the ramp is dense
 * where buildings are (2-8 storeys) and still reaches a proper tower.
 *
 * @param {number} i
 * @param {number} n
 * @returns {number}
 */
export function floorRowsFor(i, n) {
  if (n <= 1) return 4;
  const t = i / (n - 1);
  return Math.max(2, Math.round(2 * Math.pow(8, t)));
}

/**
 * The pixel layout. PURE — no canvas, no three.js, so the tests can check the
 * UV algebra in node.
 *
 * @param {{variants?: number, cellPx?: number, cols?: number, rowPx?: number}} [o]
 * @returns {AtlasLayout}
 */
export function windowAtlasLayout(o = {}) {
  const n = Math.max(1, Math.round(o.variants ?? WINDOW_ATLAS_DEFAULTS.variants));
  const px = Math.max(16, Math.round(o.cellPx ?? WINDOW_ATLAS_DEFAULTS.cellPx));
  const cols = Math.max(1, Math.round(o.cols ?? WINDOW_ATLAS_DEFAULTS.cols));
  const rowPx = Math.max(4, Math.round(o.rowPx ?? px / 4));

  /** @type {AtlasCell[]} */
  const cells = [];
  let y = 0;
  for (let i = 0; i < n; i++) {
    const rows = floorRowsFor(i, n);
    const h = rows * rowPx;
    cells.push({ index: i, rows, cols, x: 0, y, w: px, h });
    y += h;
  }
  return { width: px, height: y, cols, rowPx, cells };
}

/**
 * UV rect of one cell, in TEXTURE space (v already flipped for `flipY: true`),
 * inset by half a texel in v.
 *
 * @param {AtlasLayout} layout
 * @param {number} index Wraps, like `Billboards.frameUVRect`.
 * @returns {UVRect}
 */
export function windowAtlasUV(layout, index) {
  const n = layout.cells.length;
  const c = layout.cells[((index % n) + n) % n];
  const H = layout.height;
  const half = 0.5 / H;
  // Canvas y grows downward; texture v grows upward.
  const v0 = 1 - (c.y + c.h) / H + half;
  const v1 = 1 - c.y / H - half;
  return { u0: 0, v0, u1: 1, v1 };
}

/**
 * @typedef {Object} WindowAtlas
 * @property {THREE.CanvasTexture} texture
 * @property {AtlasLayout} layout
 * @property {AtlasCell[]} variants
 * @property {number} bayMetres Metres of facade per window bay.
 * @property {number} cellMetres Metres of facade per horizontal UV repeat.
 * @property {(index: number) => UVRect} uvFor
 * @property {(floors: number) => number} pickVariant Index whose row count best
 *   matches a storey count.
 * @property {() => void} dispose
 */

/**
 * Builds the atlas.
 *
 * @param {Object} [o]
 * @param {number} [o.variants=8]
 * @param {number} [o.cellPx=64] Cell WIDTH in pixels (height follows the row count).
 * @param {string|number} [o.seed='windows'] Deterministic — same seed, same glass.
 * @param {number} [o.cols=4] Window bays per horizontal repeat.
 * @param {number} [o.bayMetres=3.2]
 * @param {number} [o.rowPx] Pixels per storey (defaults to `cellPx / 4`).
 * @param {number} [o.darkChance=0.34]
 * @param {number} [o.brokenChance=0.11]
 * @param {number} [o.screenChance=0.09]
 * @param {string} [o.name='windowMasks']
 * @returns {WindowAtlas}
 */
export function makeWindowAtlas(o = {}) {
  const D = WINDOW_ATLAS_DEFAULTS;
  const variants = Math.max(1, Math.round(o.variants ?? D.variants));
  const cellPx = Math.max(16, Math.round(o.cellPx ?? D.cellPx));
  const cols = Math.max(1, Math.round(o.cols ?? D.cols));
  const bayMetres = o.bayMetres ?? D.bayMetres;
  const darkChance = o.darkChance ?? D.darkChance;
  const brokenChance = o.brokenChance ?? D.brokenChance;
  const screenChance = o.screenChance ?? D.screenChance;
  const name = o.name ?? 'windowMasks';

  const layout = windowAtlasLayout({ variants, cellPx, cols, rowPx: o.rowPx });

  if (typeof document === 'undefined') {
    throw new Error('makeWindowAtlas: needs a DOM canvas — use windowAtlasLayout() in node');
  }

  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const g = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
  g.clearRect(0, 0, canvas.width, canvas.height);

  const rng = mulberry32(hashSeed(o.seed ?? 'windows'));

  for (const cell of layout.cells) {
    drawCell(g, cell, layout.rowPx, rng, { darkChance, brokenChance, screenChance });
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.name = name;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  // The whole point of the single-column layout — see the header.
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 1;
  texture.needsUpdate = true;

  /** @param {number} i */
  const uvFor = (i) => windowAtlasUV(layout, i);

  /** @param {number} floors */
  const pickVariant = (floors) => {
    const want = Math.max(1, floors || 1);
    let best = 0;
    let bestErr = Infinity;
    for (const c of layout.cells) {
      // Relative error, not absolute: being 2 rows off matters far more on a
      // 3-storey shop than on a 16-storey tower.
      const err = Math.abs(Math.log(c.rows / want));
      if (err < bestErr) {
        bestErr = err;
        best = c.index;
      }
    }
    return best;
  };

  return {
    texture,
    layout,
    variants: layout.cells,
    bayMetres,
    cellMetres: cols * bayMetres,
    uvFor,
    pickVariant,
    dispose() {
      texture.dispose();
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawing
// ─────────────────────────────────────────────────────────────────────────────

/** Lamp colours. Warm office/tungsten first, one cold "screen" outlier. */
const LAMP = [
  [255, 196, 118],
  [255, 218, 164],
  [255, 176, 92],
  [255, 232, 196],
];
const SCREEN = [176, 208, 255];

/**
 * One variant cell: `rows x cols` windows, a transparent gutter all round, and
 * enough dead/broken glass that a wall of them does not read as graph paper.
 *
 * @param {CanvasRenderingContext2D} g
 * @param {AtlasCell} cell
 * @param {number} rowPx
 * @param {import('../types.js').Rng} rng
 * @param {{darkChance: number, brokenChance: number, screenChance: number}} p
 */
function drawCell(g, cell, rowPx, rng, p) {
  const bayW = cell.w / cell.cols;
  // Window box inside its bay/row. The margins ARE the mullions and spandrels;
  // they stay fully transparent so only glass ever glows.
  const winW = Math.max(2, Math.round(bayW * 0.56));
  const winH = Math.max(2, Math.round(rowPx * 0.5));
  const padY = Math.max(1, Math.round((rowPx - winH) / 2));

  for (let r = 0; r < cell.rows; r++) {
    // A whole floor dark now and then — a plant room, a vacant storey. Reads far
    // better than uniform per-window noise.
    const floorDark = rng() < 0.08;
    for (let c = 0; c < cell.cols; c++) {
      const x = Math.round(cell.x + c * bayW + (bayW - winW) / 2);
      const y = cell.y + r * rowPx + padY;

      // ALWAYS draw the same number of rng values per window, whatever the
      // outcome (the project's stream-stability rule).
      const roll = rng();
      const lamp = LAMP[Math.floor(rng() * LAMP.length) % LAMP.length];
      const bright = 0.62 + rng() * 0.38;
      const screen = rng() < p.screenChance;
      const broken = rng() < p.brokenChance;
      const notch = rng();

      if (floorDark || roll < p.darkChance) continue;

      const col = screen ? SCREEN : lamp;
      const a = broken ? 0.42 + bright * 0.3 : 0.72 + bright * 0.28;
      g.fillStyle = `rgba(${Math.round(col[0] * bright)},${Math.round(col[1] * bright)},${Math.round(
        col[2] * bright,
      )},${a.toFixed(3)})`;
      g.fillRect(x, y, winW, winH);

      if (broken) {
        // Punch a dark wedge out of one side: at 1 px per shard this is the
        // cheapest thing that reads as "the glass is gone" rather than "the
        // lamp is dim".
        g.clearRect(
          notch < 0.5 ? x : x + Math.ceil(winW / 2),
          y,
          Math.max(1, Math.floor(winW / 2)),
          Math.max(1, Math.floor(winH / 2)),
        );
      }
    }
  }

  // Guarantee the half-texel inset in `windowAtlasUV` can never clip glass.
  g.clearRect(cell.x, cell.y, cell.w, 1);
  g.clearRect(cell.x, cell.y + cell.h - 1, cell.w, 1);
}
